import React from 'react';
import { Pressable, StyleProp, Text, ViewStyle } from 'react-native';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import { RADIUS } from '../../assets/theme';
import { makeStyles, useAppTheme } from '../../theme/ThemeContext';

type FilterChipProps = {
  label: string;
  selected?: boolean;
  onPress?: () => void;
  icon?: React.ComponentProps<typeof MaterialCommunityIcons>['name'];
  /** Optional count shown after the label. */
  count?: number;
  style?: StyleProp<ViewStyle>;
  disabled?: boolean;
};

/** Pill-shaped filter toggle. Selected state uses the ink color, like Airbnb filters. */
export default function FilterChip({ label, selected, onPress, icon, count, style, disabled }: FilterChipProps) {
  const styles = useStyles();
  const { colors } = useAppTheme();
  const fg = selected ? colors.onInk : colors.textPrimary;

  return (
    <Pressable
      onPress={onPress}
      disabled={disabled}
      accessibilityRole="button"
      accessibilityState={{ selected: !!selected, disabled: !!disabled }}
      style={(state) => {
        const hovered = (state as { hovered?: boolean }).hovered;
        return [
          styles.chip,
          selected ? styles.selected : hovered ? styles.hovered : null,
          disabled ? styles.disabled : null,
          style,
        ];
      }}
    >
      {icon ? <MaterialCommunityIcons name={icon} size={16} color={fg} /> : null}
      <Text style={[styles.label, { color: fg }]} numberOfLines={1}>
        {label}
        {typeof count === 'number' ? <Text style={[styles.count, { color: fg }]}>{`  ${count}`}</Text> : null}
      </Text>
    </Pressable>
  );
}

const useStyles = makeStyles((c) => ({
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    minHeight: 36,
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: RADIUS.pill,
    borderWidth: 1,
    borderColor: c.border,
    backgroundColor: c.surface,
  },
  hovered: { borderColor: c.ink },
  selected: { backgroundColor: c.ink, borderColor: c.ink },
  disabled: { opacity: 0.5 },
  label: { fontSize: 14, fontWeight: '600' },
  count: { fontWeight: '400', opacity: 0.8 },
}));
